import { useMemo, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Card, Typography, Tag, Timeline, Input, Button, Space, Image, theme,
  Result, Alert, Tooltip, PageHeader, message, Spinner, type TagTone,
} from '../../ui';
import {
  Paperclip, X, Wrench, PauseCircle, AlertCircle, Clock, RotateCcw,
  Globe, Smartphone, MessageCircle, Phone, UserRound,
} from 'lucide-react';
import { request } from '../../api/client';
import { useAuthStore, useHasRole, usePermissionChecker } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';
import { formatIst } from '../../lib/time';
import { RoleBadge } from '../../components/RoleBadge';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { OrderLink } from '../../components/EntityLinks';
import { CopyValue } from '../../components/CopyValue';
import { productLabel } from '../../lib/format';
import { fileLabel } from '../../lib/fileName';
import {
  ComplaintActionModal, actionsFor, ACTION_LABEL, STATUS_LABEL, STATUS_TONE, STATUS_MEANING, statusMeaning, statusLineParts,
  SEVERITY_LABEL, SEVERITY_TONE, TERMINAL_STATUSES, type ComplaintAction, isOwnResolution, SELF_CLOSE_MESSAGE,
} from './complaintWorkflow';

/**
 * One complaint, in full.
 *
 * The page answers the question the business could not previously answer — WHO did what to this
 * complaint, WHEN, and WHY — so the trail and the people are first-class, not a footnote.
 *
 * LAYOUT. An earlier pass rendered everything as full-width `Descriptions` rows, which left two
 * thirds of a 1600px screen empty and pushed the history below the fold on every complaint. It is
 * now a two-column read: the SUBJECT on the left (what happened, the trail, the conversation) and
 * the STATE on the right (what you can do, where it stands, who is involved) in a rail that sticks
 * as the trail scrolls. On a narrow screen the rail simply stacks underneath.
 *
 * The trail and the discussion stay separate on purpose. A comment is a conversation; a trail entry
 * is a decision that moved the complaint. Mixing them is how a closure ends up buried under chatter.
 */

/** Timeline dot colour per trail action — decisions in green/red, requests amber, system grey. */
const ACTION_COLOR: Record<string, string> = {
  CREATED: 'blue', ASSIGNED: 'blue', REASSIGNED: 'blue', STARTED: 'blue',
  SENT_BACK: 'orange', INFO_PROVIDED: 'blue',
  ESCALATED: 'red', AUTO_ESCALATED: 'red', REJECTED: 'red', DISPUTED: 'red',
  RESOLVED: 'green', CLOSED: 'green', ACCEPTED: 'green',
  WITHDRAWN: 'gray', AUTO_CLOSED: 'gray', AUTO_CANCELLED: 'gray', MIGRATED: 'gray',
  REVERTED: 'purple',
};

/** A stacked label/value pair. Used in the complaint's meta grid, where values wrap freely. */
/**
 * How the complaint REACHED us, as a badge with the icon of the channel.
 *
 * It printed as the raw enum ("WEB"), which is a database value, not a fact a reader parses. The
 * channel matters: a complaint typed on a website by a dealer at midnight and one a rep filed while
 * standing in the shop are different kinds of evidence, and the icon says which before the word is
 * read. An unrecognised source keeps its own text rather than being forced into a wrong icon.
 */
const SOURCE_META: Record<string, { label: string; icon: ReactNode; tone: TagTone }> = {
  WEB: { label: 'Website', icon: <Globe size={13} />, tone: 'blue' },
  APP: { label: 'Mobile app', icon: <Smartphone size={13} />, tone: 'cyan' },
  WHATSAPP: { label: 'WhatsApp', icon: <MessageCircle size={13} />, tone: 'green' },
  CALL: { label: 'Phone call', icon: <Phone size={13} />, tone: 'gold' },
  SALES: { label: 'Filed by a rep', icon: <UserRound size={13} />, tone: 'purple' },
};

function SourceTag({ source }: { source?: string | null }) {
  if (!source) return <span className="text-subtle">&mdash;</span>;
  const m = SOURCE_META[String(source).toUpperCase()];
  return m
    ? <Tag tone={m.tone} icon={m.icon}>{m.label}</Tag>
    : <Tag>{String(source).replace(/_/g, ' ').toLowerCase()}</Tag>;
}

/**
 * A product's name, and its SKU only when the SKU says something the name does not.
 *
 * The API used to hand over one pre-joined string, so this printed "AGN · AGN 500ml" — a name, and a
 * SKU that opens with that same name. `productLabel` is the house resolver for exactly this pair: it
 * knows that where one label contains the other the LONGER one is the whole truth ("AGN 500ml"
 * alone), and keeps both only when the SKU genuinely adds something ("GreenGro Bio" / "AGN-1L").
 */
function ProductName({ name, sku, id }: { name?: string | null; sku?: string | null; id?: string | null }) {
  if (!name && !sku) return <>{id ? `#${id}` : '—'}</>;
  const { primary, secondary } = productLabel(name, sku);
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="truncate">{primary}</span>
      {secondary ? <Tag>{secondary}</Tag> : null}
    </span>
  );
}

function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="type-overline text-subtle">{label}</div>
      <div className="type-body text-content mt-0.5 wrap-break-word">{children}</div>
    </div>
  );
}

/** A label-left / value-right row for the side rail, hair-lined so a stack reads as one list. */
function RailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line py-2 last:border-0 last:pb-0 first:pt-0">
      <span className="type-label shrink-0 pt-0.5 text-muted">{label}</span>
      <div className="type-body min-w-0 text-right">{children}</div>
    </div>
  );
}

export function ComplaintDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const hasPerm = usePermissionChecker();
  const [comment, setComment] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);
  const [acting, setActing] = useState<ComplaintAction | null>(null);

  const { data: c, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['complaint', id],
    queryFn: () => request<any>({ url: `/complaints/${id}` }),
  });
  const { data: history } = useQuery({
    queryKey: ['complaint-history', id],
    queryFn: () => request<any[]>({ url: `/complaints/${id}/history` }),
  });
  const { data: comments } = useQuery({
    queryKey: ['complaint-comments', id],
    queryFn: () => request<any[]>({ url: `/complaints/${id}/comments` }),
  });
  /*
   * The people a complaint may be handed to, ANSWERED BY THE SERVER.
   *
   * This used to read `GET /users?pageSize=200` and sieve it in the browser, which failed three ways
   * at once: that endpoint returns a bare ARRAY (so `.data` was undefined), its `role` is an OBJECT
   * (so the role test compared against "[object Object]"), and the request was gated on `USER.VIEW`
   * as well — so the dropdown said "No results" to everybody. The eligibility rule was also a second
   * copy of one the server already enforces, and it read the primary role only, which made a
   * multi-role Sales Head invisible.
   */
  const { data: assignableStaff } = useQuery({
    queryKey: ['complaint-assignable', id],
    queryFn: () => request<{ id: string; fullName: string; roleCode: string }[]>({
      url: '/complaints/assignable', params: { complaintId: id },
    }),
    enabled: hasPerm('COMPLAINT.ASSIGN') && !!id,
  });
  /*
   * Name + the role as a COLOURED PILL, the house convention for a person anywhere in the app
   * (components/RoleBadge). This list is deliberately mixed — Super Admins, Admins and Sales Heads
   * together — and "· sales head" in grey lower-case made the reader parse text to tell a senior
   * from an administrator. `searchLabel` keeps type-to-search working over the plain words.
   */
  const assignees = useMemo(
    () => (assignableStaff ?? []).map((u) => ({
      value: String(u.id),
      searchLabel: `${u.fullName} ${String(u.roleCode ?? '').replace(/_/g, ' ')}`,
      label: (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{u.fullName}</span>
          <RoleBadge role={u.roleCode} />
        </span>
      ),
    })),
    [assignableStaff],
  );

  const addComment = useConfirmedMutation({ title: "Post this comment?", content: "It is added to the complaint trail under your name, and everyone on the complaint can see it.", okText: "Yes, post" }, {
    mutationFn: () => request({
      url: `/complaints/${id}/comments`,
      method: 'POST',
      data: { message: comment, attachments: pendingAttachments.length ? pendingAttachments : undefined },
    }),
    onSuccess: () => {
      setComment('');
      setPendingAttachments([]);
      qc.invalidateQueries({ queryKey: ['complaint-comments', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not post comment'),
  });

  // Who is looking — whoever resolved a complaint is refused Close by the server, so it is not offered.
  const myUserId = useAuthStore((s) => s.user?.id ?? null);
  const isSuperAdmin = useHasRole('SUPER_ADMIN');
  const closeViewer = useMemo(() => ({ userId: myUserId, isSuperAdmin }), [myUserId, isSuperAdmin]);
  const actions = useMemo(() => actionsFor(c, hasPerm, closeViewer), [c, hasPerm, closeViewer]);

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !c) {
    /*
     * SAY WHICH. One "may have been removed, or you may not have access" covered three different
     * things, and it is usually reached from a notification, so the reader could not tell whether the
     * app was broken. 404 = deleted; 403 = not theirs (typically the dealer moved to another rep after
     * the notice went out); anything else = the request failed, and trying again is the answer.
     */
    const code = (loadError as any)?.response?.status;
    const [title, sub] = code === 404
      ? ['This complaint no longer exists', 'It was deleted after you were told about it, so there is nothing to open.']
      : code === 403
        ? ["This complaint isn't yours to open", 'It is handled by someone else now — for example, the dealer was moved to another rep after you were notified.']
        : ["Couldn't load this complaint", 'Something went wrong fetching it. Check your connection and try again.'];
    return (
      <Result
        status={code === 404 || code === 403 ? 'warning' : 'error'}
        title={title}
        subTitle={sub}
        extra={<Button variant="primary" onClick={() => navigate('/complaints')}>Back to complaints</Button>}
      />
    );
  }

  const terminal = TERMINAL_STATUSES.includes(c.status);
  const orDash = (name?: string | null, fallbackId?: any) => name ?? (fallbackId ? `#${fallbackId}` : '—');
  /** A person with their role, the house way — a coloured pill beside the name — plus when they acted. */
  const person = (name?: string | null, role?: string | null, when?: string | null) => (
    <div className="flex flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5">
      <span className="type-body-medium">{name}</span>
      {role && <RoleBadge role={role} />}
      {when && <span className="type-caption block w-full text-subtle">{formatIst(when)}</span>}
    </div>
  );
  const none = (text: string) => <span className="type-body text-subtle">{text}</span>;

  const attachments: string[] = c.attachments ?? [];

  return (
    <div>
      {/*
        * Severity and status sit BESIDE the complaint number, not in the header's right-hand actions
        * slot. Stranded at the far edge of a wide screen they were a metre away from the thing they
        * describe — you had to track back across empty space to pair them up. They are properties of
        * the title, so they read as part of it.
        */}
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {c.complaintNo}
            <Tag tone={SEVERITY_TONE[c.severity]}>{SEVERITY_LABEL[c.severity] ?? c.severity}</Tag>
            <Tooltip label={statusMeaning(c.status, c)}>
              <Tag tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status] ?? c.status}</Tag>
            </Tooltip>
          </span>
        }
        subtitle={`${c.category ?? 'Complaint'} · raised ${formatIst(c.createdAt)}`}
        icon={<Wrench />}
      />

      {/* One banner at most — what is happening and who it is waiting on, before any detail. */}
      {c.status === 'AWAITING_INFO' && (
        <Alert
          className="mb-4"
          type="warning"
          title="Waiting on the complainant"
          description={
            c.viewer?.canRespond
              ? 'We have asked you for something before this can go further. Use “Reply with what was asked for” — the SLA clock is paused until you do.'
              : 'Sent back for more information. The SLA clock is paused until the complainant, or the person who raised it, replies.'
          }
        />
      )}
      {c.status !== 'AWAITING_INFO' && c.slaBreached && (
        <Alert className="mb-4" type="error" title="Past its SLA"
          description={`This complaint should have been resolved by ${formatIst(c.slaDueAt)}.`} />
      )}
      {c.status === 'RESOLVED' && (
        <Alert
          className="mb-4"
          type="info"
          title="Resolved, not yet closed"
          description={c.viewer?.canAccept
            ? 'Please confirm the problem is actually sorted, or tell us it is not. If we do not hear back it closes automatically in 7 days.'
            : 'Waiting for the complainant to accept it, or for someone with closing rights to sign it off. Whoever resolved it cannot also close it.'}
        />
      )}
      {terminal && (
        <Alert
          className="mb-4"
          type="info"
          title={`This complaint is ${String(STATUS_LABEL[c.status]).toLowerCase()}`}
          description="Nothing further can be done to it. Only a Super Admin can send it back for review, from the Approvals hub."
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* ─────────── LEFT: the subject ─────────── */}
        <div className="space-y-4 lg:col-span-2">
          <Card title="The complaint">
            <p className="type-body whitespace-pre-wrap text-content">{c.description}</p>

            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-line pt-3 sm:grid-cols-4">
              {/* Severity is already a chip in the page header; a third copy here would be noise. */}
              <Field label="Category">{c.category ?? '—'}</Field>
              <Field label="Source"><SourceTag source={c.source} /></Field>
              {/* A batch code is an IDENTIFIER — the thing a handler pastes into batch trace or reads
                  down a phone. Set as a code and click-to-copy, the house rule for identifiers, rather
                  than as prose it has to be selected out of. */}
              <Field label="Batch">
                {c.batchNo
                  ? <CopyValue value={c.batchNo} label="Batch"><span className="font-mono text-xs">{c.batchNo}</span></CopyValue>
                  : '—'}
              </Field>
              <Field label="Product">{c.productId ? <ProductName name={c.productName} sku={c.productSku} id={c.productId} /> : '—'}</Field>
              {/*
                * WHICH ORDER. Named on the complaint when the raiser picked one; otherwise the batch
                * is traced to the consignments that actually carried it to this dealer, offered as a
                * suggestion rather than asserted as fact.
                */}
              <Field label="Order">
                {c.orderId
                  ? <OrderLink id={c.orderId}>{c.orderNo ?? `#${c.orderId}`}</OrderLink>
                  : (c.relatedOrders ?? []).length
                    ? (
                      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                        {(c.relatedOrders as any[]).map((o) => <OrderLink key={o.id} id={o.id}>{o.orderNo}</OrderLink>)}
                        <Tooltip label="Not recorded on the complaint — these are the orders that carried this batch to this dealer.">
                          <span className="type-caption text-subtle">(from the batch)</span>
                        </Tooltip>
                      </span>
                    )
                    : <span className="text-subtle">—</span>}
              </Field>
            </div>

            {/*
              * THE MISMATCH. Shown only when the complaint records what actually arrived, and framed
              * as a comparison because that is what it is — our document on one side, the carton on
              * the other. A batch we have no record of manufacturing is called out in red: for an
              * agri-input business that is the first sign of a counterfeit pack, not a typo, and it
              * needs to reach a human rather than sit in a field nobody re-reads.
              */}
            {(c.receivedProductId || c.receivedBatchNo) && (
              <div className="mt-4 rounded-card border border-line bg-surface-2 p-3">
                <div className="type-body-strong mb-2">What actually arrived</div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                  <Field label="Billed">
                    <ProductName name={c.productName} sku={c.productSku} id={c.productId} />
                    {c.batchNo ? <span className="ml-1 font-mono text-xs text-muted">{c.batchNo}</span> : null}
                  </Field>
                  <Field label="Received">
                    {c.receivedProductId
                      ? <ProductName name={c.receivedProductName} sku={c.receivedProductSku} id={c.receivedProductId} />
                      : <span className="text-subtle">same product</span>}
                    {c.receivedBatchNo ? <span className="ml-1 font-mono text-xs text-muted">{c.receivedBatchNo}</span> : null}
                  </Field>
                  <Field label="Document">{c.documentRef || '—'}</Field>
                </div>
                {c.receivedBatchKnown === false && (
                  <Alert
                    className="mt-3"
                    type="error"
                    title={`Batch ${c.receivedBatchNo} is not one we have on record`}
                    description="No batch with this code exists in stock. Treat this as a possible counterfeit or mislabelled pack and verify the physical stock before settling the claim."
                  />
                )}
                {c.receivedBatchKnown === true && (
                  <p className="type-caption mt-2 text-muted">
                    Batch {c.receivedBatchNo} is a batch we manufactured — this looks like a despatch
                    or picking error rather than a counterfeit.
                  </p>
                )}
              </div>
            )}

            {attachments.length > 0 && (
              <div className="mt-4 border-t border-line pt-3">
                <div className="type-overline mb-2 text-subtle">
                  Evidence · {attachments.length} file{attachments.length === 1 ? '' : 's'}
                </div>
                {/* The house preview kit: a thumbnail for every file — a PDF included — that opens
                    the arrow-key gallery. It used to draw an image inline but print a NON-image as a
                    button carrying the stored filename ("1788868764660-1690989a.pdf"), which is a
                    generated key, not a description, and told the reader nothing about the evidence
                    they were being asked to judge. */}
                {/* Same 44px tile as the trail below it. At 72 the evidence strip read as the loudest
                    thing on the card, which it is not — it is one supporting fact among five, and the
                    tile only has to be big enough to recognise and click. */}
                <AttachmentCell files={attachments} label="Evidence" size={44} maxThumbs={6} />
              </div>
            )}
          </Card>

          <Card title="History" extra={<span className="type-caption text-subtle">Every change, who made it and why</span>}>
            {(history ?? []).length === 0 ? (
              <Typography.Text type="secondary">Nothing recorded yet.</Typography.Text>
            ) : (
              <Timeline
                items={(history ?? []).map((h: any) => ({
                  color: ACTION_COLOR[h.action] ?? 'blue',
                  children: (
                    <div className="pb-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="type-body-strong">{ACTION_LABEL[h.action] ?? h.action}</span>
                        {/* WHO IT WENT TO. The row used to name only the person who clicked, so an
                            assignment to Vikram read "Assigned · Super Admin" and the owner appeared
                            nowhere in the history. */}
                        {h.handedToName ? (
                          <span className="inline-flex flex-wrap items-center gap-1 type-body">
                            <span className="text-muted">{h.action === 'STARTED' ? '— taken on by' : 'to'}</span>
                            <span className="type-body-medium text-content">{h.handedToName}</span>
                            {h.handedToRole && <RoleBadge role={h.handedToRole} />}
                          </span>
                        ) : h.handedToDesk ? (
                          <span className="type-body text-muted">to the Admin desk</span>
                        ) : null}
                        {h.fromStatus && h.fromStatus !== h.toStatus && (
                          <span className="type-caption text-subtle">
                            {STATUS_LABEL[h.fromStatus] ?? h.fromStatus} → {STATUS_LABEL[h.toStatus] ?? h.toStatus}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        <span className="type-caption text-subtle">by</span>
                        <span className="type-caption text-muted">{h.changedByName}</span>
                        {h.changedByRole && <RoleBadge role={h.changedByRole} />}
                        <span className="type-caption text-subtle">· {formatIst(h.createdAt)}</span>
                      </div>
                      {h.remarks && (
                        <div className="type-body mt-1 whitespace-pre-wrap text-content">{h.remarks}</div>
                      )}
                      {(h.attachments ?? []).length > 0 && (
                        <div className="mt-1.5">
                          {/* Was a button that simply said "Attachment" — the same five letters whether it
                              was a photo of a burst sack or a lab report. A thumbnail says which. */}
                          <AttachmentCell files={h.attachments as string[]} label="Attachment" size={44} maxThumbs={4} />
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            )}
          </Card>

          <Card title="Discussion">
            {(comments ?? []).length === 0 && (
              <Typography.Text type="secondary">No comments yet.</Typography.Text>
            )}
            <Timeline
              items={(comments ?? []).map((com: any) => ({
                color: 'blue',
                children: (
                  <div className="pb-1">
                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <span className="type-body-strong">{com.authorName}</span>
                      {com.authorRole && <RoleBadge role={com.authorRole} />}
                      <span className="type-caption text-subtle">{formatIst(com.createdAt)}</span>
                    </div>
                    <div className="type-body mt-1 whitespace-pre-wrap text-content">{com.message}</div>
                    {(com.attachments ?? []).length > 0 && (
                      <div className="mt-1.5">
                        <AttachmentCell files={com.attachments as string[]} label="Attachment" size={48} maxThumbs={4} />
                      </div>
                    )}
                  </div>
                ),
              }))}
            />

            {/* A finished complaint's thread is read-only — the server refuses the post, so don't offer it. */}
            {terminal ? (
              <Typography.Text type="secondary">
                This complaint is {String(STATUS_LABEL[c.status]).toLowerCase()}, so the discussion is closed.
              </Typography.Text>
            ) : (
              <>
                <Input.TextArea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment…"
                  rows={2}
                  style={{ marginBottom: 8 }}
                />
                {pendingAttachments.length > 0 && (
                  <Space size={6} wrap style={{ marginBottom: 8 }}>
                    {pendingAttachments.map((url, i) => (
                      <Tag key={url}>
                        <Paperclip size={16} />
                        {fileLabel(url, { index: i + 1 })}
                        <X size={16} className="cursor-pointer"
                          onClick={() => setPendingAttachments((arr) => arr.filter((u) => u !== url))} />
                      </Tag>
                    ))}
                  </Space>
                )}
                <Space>
                  <FileUploadField
                    category="attachment"
                    buttonLabel="Attach file"
                    preview={false}
                    value=""
                    onChange={(url) => { if (url) setPendingAttachments((arr) => [...arr, url]); }}
                  />
                  <Button
                    variant="primary"
                    onClick={() => addComment.mutate()}
                    disabled={!comment && pendingAttachments.length === 0}
                    loading={addComment.isPending}
                  >
                    Post
                  </Button>
                </Space>
              </>
            )}
          </Card>
        </div>

        {/* ─────────── RIGHT: the state. Sticks while the trail scrolls. ─────────── */}
        {/* FIRST on a phone. Stacked in source order, the buttons sat below the complaint, the whole
            history and the discussion — someone opening "Complaint … is yours" from a notification had
            to scroll to find what to press. On a wide screen it is the sticky right rail as before. */}
        <div className="order-first space-y-4 lg:order-0 lg:sticky lg:top-4 lg:self-start">
          {actions.length > 0 && (
            <Card title="What you can do" density="compact">
              <div className="flex flex-col gap-2">
                {actions.map((a) => (
                  <Button
                    key={a.key}
                    block
                    variant={a.danger ? 'soft-danger' : a.primary ? 'primary' : 'default'}
                    icon={a.icon}
                    onClick={() => setActing(a)}
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
              <p className="type-caption mt-2 text-subtle">
                Whatever you do here is recorded in the history under your name.
              </p>
            </Card>
          )}
          {actions.length === 0 && !terminal && (
            <Card density="compact">
              <p className="type-body text-muted">
                {c.status === 'RESOLVED' && hasPerm('COMPLAINT.CLOSE') && isOwnResolution(c, closeViewer)
                  ? SELF_CLOSE_MESSAGE
                  /* A resolved complaint waits on the complainant or a closer — never "with" its resolver,
                     who read their own name in the third person here (found on the phone, 26 Sep). */
                  : c.status === 'RESOLVED'
                    ? 'Resolved — now waiting for the complainant to accept it, or for a Sales Head or Admin to close it.'
                    : c.assignedToName && String(c.assignedToUserId) !== String(myUserId)
                      ? `Nothing is waiting on you. This complaint is with ${c.assignedToName} — only they, or an Admin, can work it.`
                      : 'Nothing is waiting on you here.'}
              </p>
            </Card>
          )}

          <Card title="Status" density="compact">
            <div className="border-b border-line pb-2">
              <div className="flex items-center justify-between gap-3">
                <span className="type-label text-muted">Now</span>
                <Tag tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status] ?? c.status}</Tag>
              </div>
              {/* Named AND badged, because this page knows who — see statusLineParts(). */}
              <p className="type-caption mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-subtle">
                {(() => {
                  const { prefix, name, role, suffix } = statusLineParts(c.status, c);
                  return (
                    <>
                      <span>{prefix}</span>
                      {name ? <span className="text-content">{name}</span> : null}
                      {name && role ? <RoleBadge role={role} /> : null}
                      {suffix ? <span>{suffix}</span> : null}
                    </>
                  );
                })()}
              </p>
            </div>
            <RailRow label="SLA">
              {c.status === 'AWAITING_INFO' ? (
                <span className="inline-flex items-center gap-1 text-muted"><PauseCircle size={16} /> Paused</span>
              ) : c.slaDueAt ? (
                <span className={c.slaBreached ? 'text-danger-600' : ''}>
                  {c.slaBreached ? <AlertCircle size={16} className="mr-1 inline" /> : <Clock size={16} className="mr-1 inline" />}
                  {formatIst(c.slaDueAt)}
                </span>
              ) : none('—')}
            </RailRow>
            {Number(c.reopenCount) > 0 && (
              <RailRow label="Reopened">
                <span className="inline-flex items-center gap-1">
                  <RotateCcw size={16} /> {c.reopenCount}×
                </span>
              </RailRow>
            )}
            {c.customerRating ? (
              <RailRow label="Rating">
                <span style={{ color: 'var(--color-harvest-400)' }}>
                  {'★'.repeat(c.customerRating)}{'☆'.repeat(5 - c.customerRating)}
                </span>
              </RailRow>
            ) : null}
          </Card>

          <Card title="People" density="compact">
            <RailRow label="Raised by">
              {person(orDash(c.raisedByName, c.raisedByUserId), c.raisedByRole, c.createdAt)}
            </RailRow>
            {(c.retailerId || c.farmerId) && (
              <RailRow label="Raised for">
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <span className="type-body-medium">
                    {c.retailerId ? orDash(c.retailerName, c.retailerId) : orDash(c.farmerName, c.farmerId)}
                  </span>
                  <Tag tone={c.retailerId ? 'blue' : 'green'}>{c.retailerId ? 'Retailer' : 'Farmer'}</Tag>
                  {c.retailerMobile && <span className="type-caption block w-full text-subtle">{c.retailerMobile}</span>}
                </div>
              </RailRow>
            )}
            <RailRow label="With">
              {c.assignedToUserId ? (
                <>
                  {person(orDash(c.assignedToName, c.assignedToUserId), c.assignedToRole, c.assignedAt)}
                  {c.assignedByName && (
                    <div className="type-caption text-subtle">assigned by {c.assignedByName}</div>
                  )}
                </>
              ) : <Tag tone="gold">Unassigned</Tag>}
            </RailRow>
            <RailRow label="Resolved by">
              {c.resolvedAt
                ? person(orDash(c.resolvedByName, c.resolvedBy), c.resolvedByRole, c.resolvedAt)
                : none('Not resolved yet')}
            </RailRow>
            <RailRow label="Closed by">
              {c.closedAt
                ? (c.closedBy
                    ? person(orDash(c.closedByName, c.closedBy), c.closedByRole, c.closedAt)
                    // closed_by NULL is a background sweep, never a person. Saying "System" is the
                    // honest answer; inventing a name for an automatic close would be worse than none.
                    : person('System', null, c.closedAt))
                : none('Not closed yet')}
            </RailRow>
          </Card>

          {(c.resolutionNotes || c.closureRemarks) && (
            <Card title="Outcome" density="compact">
              {c.resolutionNotes && (
                <div className="border-b border-line pb-2">
                  <div className="type-overline text-subtle">What was done</div>
                  <p className="type-body mt-0.5 whitespace-pre-wrap text-content">{c.resolutionNotes}</p>
                </div>
              )}
              {c.closureRemarks && (
                <div className={c.resolutionNotes ? 'pt-2' : ''}>
                  <div className="type-overline text-subtle">Closure remarks</div>
                  <p className="type-body mt-0.5 whitespace-pre-wrap text-content">{c.closureRemarks}</p>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      <ComplaintActionModal
        open={!!acting}
        action={acting}
        complaint={c}
        assignees={assignees}
        onClose={() => setActing(null)}
      />
    </div>
  );
}
